import { TERMS_VERSION, PRIVACY_VERSION } from "./policy-versions";

/**
 * Runs post-authentication provisioning steps:
 * 1. Provisions a business for the user (creates one if needed)
 * 2. Saves the session to multi-account store
 * 3. Records terms agreement if pending (flag set on login page before OAuth)
 *
 * Used by both:
 * - /auth/callback (web OAuth redirect flow)
 * - Login page native Google sign-in flow
 */
export async function runPostAuthProvisioning(): Promise<void> {
  // Read identifiers stashed before OAuth redirect (for provisional account merge)
  const pendingPhone =
    typeof window !== "undefined"
      ? localStorage.getItem("pending_phone_link")
      : null;
  const pendingIgAccountId =
    typeof window !== "undefined"
      ? localStorage.getItem("pending_ig_link")
      : null;
  const pendingDemoToken =
    typeof window !== "undefined"
      ? localStorage.getItem("pending_demo_token")
      : null;

  // Ensure user has a business (creates one if needed).
  // If identifiers are provided, the provision route will merge any matching
  // provisional accounts into this user's business.
  const provisionBody: Record<string, string> = {};
  if (pendingPhone) provisionBody.phone = pendingPhone;
  if (pendingIgAccountId) provisionBody.ig_account_id = pendingIgAccountId;
  if (pendingDemoToken) provisionBody.demo_session_token = pendingDemoToken;

  const hasBody = Object.keys(provisionBody).length > 0;
  const provisionRes = await fetch("/api/auth/provision", {
    method: "POST",
    headers: hasBody ? { "Content-Type": "application/json" } : {},
    body: hasBody ? JSON.stringify(provisionBody) : undefined,
  });

  if (pendingPhone) localStorage.removeItem("pending_phone_link");
  if (pendingIgAccountId) localStorage.removeItem("pending_ig_link");
  if (pendingDemoToken) localStorage.removeItem("pending_demo_token");

  // Set locale cookie from business preferred_language (e.g., provisional account
  // that was started in Portuguese should show Portuguese after sign-up)
  try {
    if (provisionRes.ok) {
      const provisionData = await provisionRes.json();
      if (provisionData.preferred_language && provisionData.preferred_language !== "en") {
        await fetch("/api/locale", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locale: provisionData.preferred_language }),
        });
      }
    }
  } catch {
    // Non-fatal — locale can be changed later
  }

  // Save session to multi-account store
  try {
    await fetch("/api/auth/sessions/add", { method: "POST" });
  } catch {
    // Non-fatal
  }

  // Record terms agreement for OAuth sign-ups
  // The flag is set on the login page before initiating OAuth
  if (
    typeof window !== "undefined" &&
    localStorage.getItem("pending_terms_agreement")
  ) {
    try {
      await fetch("/api/auth/agreement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          terms_version: TERMS_VERSION,
          privacy_version: PRIVACY_VERSION,
        }),
      });
      // Set the signed policy cookie so middleware doesn't redirect
      await fetch("/api/policy-cookie", { method: "POST" });
    } catch {
      // Non-fatal — agreement can be re-prompted later
    }
    localStorage.removeItem("pending_terms_agreement");
  }
}
